import React, { useEffect } from 'react';

/**
 * MobileModal - A modal that becomes a full-screen sheet on mobile devices
 * 
 * Props:
 * - isOpen: boolean - Whether the modal is visible
 * - onClose: function - Called when the modal should close
 * - title: string - Modal title
 * - children: ReactNode - Modal content
 * - showHeader: boolean (default true) - Whether to show the header
 * - maxWidth: string (default "max-w-2xl") - Max width on desktop
 * - zIndex: number (default 50) - Stack order for nested modals
 */
export default function MobileModal({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  showHeader = true,
  maxWidth = "max-w-2xl",
  zIndex = 50
}) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0" style={{ zIndex }}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 sm:bg-black/40"
        onClick={onClose}
      />
      
      {/* Modal Container - entire modal is scrollable */}
      <div 
        className="absolute inset-0 sm:inset-4 sm:top-[5vh] sm:bottom-[5vh] sm:left-1/2 sm:-translate-x-1/2 sm:w-full bg-white sm:rounded-xl shadow-2xl flex flex-col animate-slide-up sm:animate-none overflow-y-auto overscroll-contain pt-[env(safe-area-inset-top,0px)] sm:pt-0"
        style={{ maxWidth: 'min(calc(100vw - 2rem), 42rem)' }}
      >
        <div className={`w-full mx-auto flex flex-col min-h-full`}>
          {/* Header - sticky at top */}
          {showHeader && (
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-200 bg-white sticky top-0 z-10">
              {/* Mobile drag indicator */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full sm:hidden" />
              
              <h2 className="text-lg sm:text-xl font-bold text-theme-primary mt-2 sm:mt-0">
                {title}
              </h2>
              <button
                onClick={onClose}
                className="p-2 -mr-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
          
          {/* Content - no separate scroll, modal container handles it */}
          <div className="flex-1">
            <div className="p-4 sm:p-6 pb-[max(2rem,env(safe-area-inset-bottom,0.75rem))]">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * MobileCard - A card component for displaying list items on mobile
 * Converts table rows to stacked cards on small screens
 */
export function MobileCard({ children, className = "", onClick }) {
  return (
    <div 
      className={`bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''} ${className}`}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

/**
 * MobileCardField - A field within a MobileCard
 */
export function MobileCardField({ label, value, className = "" }) {
  return (
    <div className={`flex justify-between items-center py-1 ${className}`}>
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  );
}

/**
 * ResponsiveGrid - A grid that stacks on mobile
 */
export function ResponsiveGrid({ children, cols = 3, className = "" }) {
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${cols} gap-4 ${className}`}>
      {children}
    </div>
  );
}

